/**
 * scripts-components-facets.js
 * -----------------------------------------------------------------------
 * Collection page behavior:
 *  - `<am-filter-group>`: independent (multi-open) collapsible primitive
 *    for desktop filter groups — deliberately separate from
 *    `<am-disclosure>` (header/mobile-nav) since that component closes
 *    its siblings on open, which filter groups must NOT do.
 *  - Facet auto-submit: checking/unchecking a desktop filter, or changing
 *    the sort dropdown, submits the facets form immediately (progressive
 *    enhancement — the plain `<form method="get">` already works without
 *    JS). The mobile drawer intentionally does NOT auto-submit; it only
 *    applies on the explicit "Apply" button so shoppers can stage several
 *    changes at once.
 *  - Mobile "Clear" navigates straight to the filter-free collection URL.
 *  - Infinite scroll: progressive enhancement over the always-rendered,
 *    fully functional Shopify `{{ paginate }}` links. Only activates on
 *    grids opted in via `data-infinite-scroll`; fetches and appends the
 *    next page, replacing the pagination nav with the freshly fetched one
 *    so it keeps working (or stops) exactly like normal pagination would.
 * ----------------------------------------------------------------------- */
(function () {
  'use strict';

  var dom = window.AniMarket && window.AniMarket.dom;

  /* --------------------------------------------------------------------
   * <am-filter-group>
   * ------------------------------------------------------------------ */
  class AmFilterGroupElement extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-disclosure-trigger]');
      this.panel = this.querySelector('[data-disclosure-panel]');
      if (!this.trigger || !this.panel) return;

      var isOpen = this.hasAttribute('open');
      this.trigger.setAttribute('aria-expanded', String(isOpen));
      this.trigger.addEventListener('click', this._onTriggerClick.bind(this));
    }

    _onTriggerClick() {
      var willOpen = !this.hasAttribute('open');
      this.toggleAttribute('open', willOpen);
      this.trigger.setAttribute('aria-expanded', String(willOpen));
    }
  }

  if (!customElements.get('am-filter-group')) {
    customElements.define('am-filter-group', AmFilterGroupElement);
  }

  /* --------------------------------------------------------------------
   * Desktop auto-submit (facets + sort)
   * ------------------------------------------------------------------ */
  var desktopForm = document.getElementById('FacetFiltersForm');

  function submitForm(form) {
    if (!form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  }

  if (desktopForm) {
    var handleChange = dom && dom.debounce ? dom.debounce(function () {
      submitForm(desktopForm);
    }, 200) : function () {
      submitForm(desktopForm);
    };

    desktopForm.addEventListener('change', function (event) {
      if (event.target.matches('[data-facet-input]')) handleChange();
    });
  }

  var sortSelect = document.querySelector('[data-sort-select]');
  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      submitForm(sortSelect.form);
    });
  }

  /* --------------------------------------------------------------------
   * Mobile drawer: Clear button navigates to the filter-free URL
   * ------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var clearButton = event.target.closest('[data-facets-clear]');
    if (!clearButton) return;
    var form = clearButton.closest('form');
    if (form && form.action) window.location.href = form.action;
  });

  /* --------------------------------------------------------------------
   * View toggle (grid/list) — architecture only; persists the chosen
   * view per browser so it survives pagination/navigation.
   * ------------------------------------------------------------------ */
  var VIEW_STORAGE_KEY = 'am:collection-view';

  function setView(view) {
    document.querySelectorAll('[data-product-grid]').forEach(function (grid) {
      grid.setAttribute('data-view', view);
    });
    document.querySelectorAll('[data-view-option]').forEach(function (button) {
      var isActive = button.getAttribute('data-view-option') === view;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, view);
    } catch (error) {
      /* localStorage unavailable (private mode, etc.) — view just won't persist */
    }
  }

  document.addEventListener('click', function (event) {
    var viewButton = event.target.closest('[data-view-option]');
    if (viewButton) setView(viewButton.getAttribute('data-view-option'));
  });

  (function restoreView() {
    var storedView;
    try {
      storedView = window.localStorage.getItem(VIEW_STORAGE_KEY);
    } catch (error) {
      storedView = null;
    }
    if (storedView) setView(storedView);
  })();

  /* --------------------------------------------------------------------
   * Infinite scroll (progressive enhancement over real pagination links)
   * ------------------------------------------------------------------ */
  var SKELETON_COUNT = 4;

  function buildSkeletonWrapper(root) {
    var template = root.querySelector('[data-product-card-skeleton-template]');
    var wrapper = document.createElement('div');
    wrapper.className = 'product-grid__loading-skeletons';
    for (var i = 0; i < SKELETON_COUNT; i++) {
      if (template) {
        wrapper.appendChild(template.content.cloneNode(true));
      }
    }
    return wrapper;
  }

  function initInfiniteScroll(root) {
    var grid = root.querySelector('[data-product-grid]');
    var nav = root.querySelector('[data-pagination]');
    if (!grid || !nav) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) loadNextPage(entry.target);
      });
    }, { rootMargin: '400px' });

    var sentinel = root.querySelector('[data-infinite-scroll-sentinel]');
    if (sentinel) observer.observe(sentinel);

    function loadNextPage(currentSentinel) {
      observer.unobserve(currentSentinel);
      var currentNav = root.querySelector('[data-pagination]');
      var nextUrl = currentNav && currentNav.getAttribute('data-pagination-next');
      if (!nextUrl) return;

      var skeletonWrapper = buildSkeletonWrapper(root);
      grid.after(skeletonWrapper);

      fetch(nextUrl)
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var parsedDocument = new DOMParser().parseFromString(html, 'text/html');
          var newGrid = parsedDocument.querySelector('[data-product-grid]');
          var newNav = parsedDocument.querySelector('[data-pagination]');

          if (newGrid) {
            Array.prototype.forEach.call(newGrid.children, function (card) {
              grid.appendChild(card);
            });
            // Newly appended cards render with a static
            // aria-pressed="false" wishlist button since Liquid has no
            // knowledge of the client's localStorage wishlist state.
            if (window.AniMarket && window.AniMarket.wishlist && window.AniMarket.wishlist.sync) {
              window.AniMarket.wishlist.sync();
            }
          }

          skeletonWrapper.remove();

          if (newNav) {
            currentNav.replaceWith(newNav);
            var newSentinel = newNav.parentElement.querySelector('[data-infinite-scroll-sentinel]');
            if (newSentinel) observer.observe(newSentinel);
          } else {
            currentNav.remove();
          }
        })
        .catch(function () {
          skeletonWrapper.remove();
          // Re-observe so scrolling back into view retries the request
          // instead of silently giving up after a network hiccup.
          if (currentSentinel.isConnected) observer.observe(currentSentinel);
        });
    }
  }

  document.querySelectorAll('[data-collection-root][data-infinite-scroll]').forEach(initInfiniteScroll);
})();
